// iter425 — Reconcile This Whole Month dialog.
// Shows every still-unlinked bank-statement txn for a date range,
// grouped by best-guess payee. Lets the user wipe out the unmatched
// queue for a whole period in 4-5 clicks: tick groups → bulk-create
// payments per payee → done.
import { cur } from '@/lib/currency';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, Banknote, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { toLocalYmd } from '@/lib/dateRange';

const fmtSR = (n) => `${cur()} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PERIOD_OPTIONS = [
  { v: 'this_month', l: 'This month' },
  { v: 'last_month', l: 'Last month' },
  { v: 'last_3_months', l: 'Last 3 months' },
  { v: 'last_6_months', l: 'Last 6 months' },
  { v: 'last_12_months', l: 'Last 12 months' },
  { v: 'ytd', l: 'Year to date' },
  { v: 'custom', l: 'Custom range' },
];

function periodToDates(period, dateFrom, dateTo) {
  const today = new Date();
  const fmt = (d) => toLocalYmd(d);
  if (period === 'this_month') {
    const f = new Date(today.getFullYear(), today.getMonth(), 1);
    const t = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return [fmt(f), fmt(t)];
  }
  if (period === 'last_month') {
    const f = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const t = new Date(today.getFullYear(), today.getMonth(), 0);
    return [fmt(f), fmt(t)];
  }
  if (period === 'last_3_months') {
    const f = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    const t = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return [fmt(f), fmt(t)];
  }
  if (period === 'last_6_months') {
    const f = new Date(today.getFullYear(), today.getMonth() - 5, 1);
    const t = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return [fmt(f), fmt(t)];
  }
  if (period === 'last_12_months') {
    const f = new Date(today.getFullYear(), today.getMonth() - 11, 1);
    const t = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return [fmt(f), fmt(t)];
  }
  if (period === 'ytd') {
    const f = new Date(today.getFullYear(), 0, 1);
    const t = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return [fmt(f), fmt(t)];
  }
  return [dateFrom || '', dateTo || ''];
}

export function ReconcileMonthDialog({ open, onOpenChange, onApplied }) {
  const [period, setPeriod] = useState('this_month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [direction, setDirection] = useState('all');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // {totals, groups}
  const [selectedGroups, setSelectedGroups] = useState({}); // key: `${payee_type}:${payee_id}` → bool
  const [applying, setApplying] = useState(false);
  const [appliedSummary, setAppliedSummary] = useState(null);

  // Fetch the preview when the dialog opens or filters change.
  const fetchPreview = async () => {
    const [from, to] = periodToDates(period, dateFrom, dateTo);
    if (!from || !to) {
      toast.error('Pick a date range first');
      return;
    }
    setLoading(true);
    setAppliedSummary(null);
    try {
      const r = await api.get('/banking/reconcile-month/preview', {
        params: { period_start: from, period_end: to, direction },
      });
      setData(r.data);
      setSelectedGroups({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setData(null);
      setSelectedGroups({});
      setAppliedSummary(null);
      return;
    }
    fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groupKey = (g) => `${g.payee_type}:${g.payee_id || ''}`;

  // Linkable groups (not the "unmatched" bucket — those have no entity
  // to attach to, so the bulk-create endpoint can't process them here).
  const linkableGroups = useMemo(
    () => (data?.groups || []).filter((g) => g.payee_type !== 'unmatched'),
    [data],
  );
  const selectedKeys = Object.keys(selectedGroups).filter((k) => selectedGroups[k]);
  const selectedTotals = useMemo(() => {
    let txns = 0; let total = 0;
    for (const g of linkableGroups) {
      if (selectedGroups[groupKey(g)]) { txns += g.txn_count; total += g.total_amount; }
    }
    return { txns, total };
  }, [linkableGroups, selectedGroups]);

  const toggleAll = (checked) => {
    if (checked) {
      const next = {};
      linkableGroups.forEach((g) => { next[groupKey(g)] = true; });
      setSelectedGroups(next);
    } else {
      setSelectedGroups({});
    }
  };

  const apply = async () => {
    if (selectedKeys.length === 0) return;
    setApplying(true);
    let created = 0; let skipped = 0; let totalAmount = 0;
    const failed = [];
    try {
      // One bulk-from-stmt-txns call per group — each call creates
      // one bank_payment per included statement-txn (clean audit trail)
      // and is idempotent (already-linked txns are skipped).
      for (const g of linkableGroups) {
        if (!selectedGroups[groupKey(g)]) continue;
        const refs = g.rows.map((r) => ({ statement_id: r.statement_id, txn_index: r.txn_index }));
        try {
          const r = await api.post('/banking/bank-payments/bulk-from-stmt-txns', {
            payee_type: g.payee_type,
            payee_id: g.payee_id,
            txn_refs: refs,
            description: '[Reconcile] auto-link from preview',
          });
          created += r.data?.created_count || 0;
          skipped += r.data?.skipped_count || 0;
          totalAmount += r.data?.total_amount || 0;
        } catch (e) {
          failed.push(`${g.payee_name}: ${e?.response?.data?.detail || 'failed'}`);
        }
      }
      setAppliedSummary({ created, skipped, totalAmount, failed });
      if (created > 0) {
        toast.success(`Created ${created} bank payment${created > 1 ? 's' : ''} totalling ${fmtSR(totalAmount)}`);
      }
      if (skipped > 0) toast.info(`${skipped} txn${skipped > 1 ? 's' : ''} skipped (already linked)`);
      if (failed.length > 0) toast.error(`${failed.length} group${failed.length > 1 ? 's' : ''} failed — see summary`);
      // Refresh so the just-linked rows disappear.
      await fetchPreview();
      if (typeof onApplied === 'function') onApplied();
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90dvh] flex flex-col" data-testid="reconcile-month-dialog">
        <DialogHeader>
          <DialogTitle>Reconcile this whole month</DialogTitle>
          <DialogDescription>
            Every unlinked bank-statement txn in the period is grouped by best-guess payee.
            Tick the groups you want to wipe out and we'll bulk-create the bank payments in one shot.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-2 border-b pb-3">
          <div>
            <Label className="text-xs">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="mt-1 w-40" data-testid="rec-month-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {period === 'custom' && (
            <>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 w-40" data-testid="rec-month-from" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 w-40" data-testid="rec-month-to" />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">Direction</Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="mt-1 w-32" data-testid="rec-month-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="outgoing">Outgoing</SelectItem>
                <SelectItem value="incoming">Incoming</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={fetchPreview}
            disabled={loading}
            data-testid="rec-month-refresh"
            className="h-9"
          >
            {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />}
            Refresh
          </Button>
        </div>

        {/* Totals strip */}
        {data && (
          <div className="flex flex-wrap items-center gap-2 text-xs py-2 border-b" data-testid="rec-month-totals">
            <Badge variant="outline">Scanned {data.totals.scanned}</Badge>
            <Badge className="bg-emerald-100 text-emerald-700">Linkable {data.totals.linkable}</Badge>
            <Badge className="bg-amber-100 text-amber-700">Unmatched {data.totals.unmatched}</Badge>
            <span className="ml-auto text-muted-foreground">
              Total linkable: <span className="font-bold tabular-nums">{fmtSR(data.totals.total_linkable_amount)}</span>
            </span>
          </div>
        )}

        {/* iter426 — Confidence legend so the colour key is discoverable
            even when a window has 0 linkable groups (common after the
            cron has caught up). */}
        {data && (
          <div className="text-[10px] text-muted-foreground flex items-center gap-3 -mt-1" data-testid="rec-confidence-legend">
            <span className="font-semibold uppercase tracking-wide">Confidence</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-emerald-500" /> ≥95% account-match (auto-apply candidate)</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-amber-500" /> 80-94% mixed</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-rose-500" /> &lt;80% name-only</span>
          </div>
        )}

        {/* Groups */}
        <div className="flex-1 overflow-y-auto -mx-2 px-2 py-2 space-y-2">
          {loading && !data && (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              <Loader2 size={20} className="animate-spin mr-2" /> Scanning your bank statements…
            </div>
          )}
          {data && linkableGroups.length === 0 && (
            <div className="text-center py-10 text-muted-foreground text-sm">
              No linkable groups for this window. Either everything is already reconciled or the unmatched bucket holds the remaining txns.
            </div>
          )}
          {linkableGroups.length > 0 && (
            <div className="flex items-center gap-2 sticky top-0 bg-background py-1 border-b text-xs">
              <input
                type="checkbox"
                aria-label="Select all"
                data-testid="rec-month-select-all"
                checked={linkableGroups.every((g) => selectedGroups[groupKey(g)])}
                onChange={(e) => toggleAll(e.target.checked)}
              />
              <span className="text-muted-foreground">Select all linkable groups</span>
              {selectedKeys.length > 0 && (
                <span className="ml-auto text-foreground font-semibold">
                  {selectedKeys.length} group{selectedKeys.length > 1 ? 's' : ''} · {selectedTotals.txns} txns · {fmtSR(selectedTotals.total)}
                </span>
              )}
            </div>
          )}
          {linkableGroups.map((g) => {
            const k = groupKey(g);
            const sel = !!selectedGroups[k];
            return (
              <div
                key={k}
                className={`rounded-md border p-2 ${sel ? 'border-primary bg-primary/5' : 'border-border'}`}
                data-testid={`rec-group-${k}`}
              >
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={sel}
                    onChange={(e) => setSelectedGroups((prev) => ({ ...prev, [k]: e.target.checked }))}
                    data-testid={`rec-group-check-${k}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <Badge className="text-[9px] uppercase">{g.payee_type}</Badge>
                      <span className="font-semibold text-sm">{g.payee_name}</span>
                      {/* iter426 — confidence pill (≥95% green = auto-apply candidate) */}
                      {typeof g.confidence === 'number' && g.confidence > 0 && (
                        <span
                          className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                            g.confidence >= 95
                              ? 'bg-emerald-100 text-emerald-700'
                              : g.confidence >= 80
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-rose-100 text-rose-700'
                          }`}
                          data-testid={`rec-group-confidence-${groupKey(g)}`}
                          title={`${g.match_via_counts?.account || 0}/${g.txn_count} matched by account number, ${g.match_via_counts?.description || 0}/${g.txn_count} by name`}
                        >
                          {g.confidence}% {g.primary_match_via}
                        </span>
                      )}
                      <span className="ml-auto text-xs text-muted-foreground">
                        {g.txn_count} txn{g.txn_count > 1 ? 's' : ''} · <span className="font-bold tabular-nums">{fmtSR(g.total_amount)}</span>
                      </span>
                    </div>
                    <div className="mt-1 max-h-28 overflow-y-auto text-[11px] text-muted-foreground space-y-0.5">
                      {g.rows.slice(0, 6).map((r) => (
                        <div key={`${r.statement_id}-${r.txn_index}`} className="truncate flex items-baseline gap-2">
                          <span className="text-foreground/70 whitespace-nowrap">{r.date}</span>
                          <span className="truncate flex-1">{r.description || '—'}</span>
                          <span className={`font-mono tabular-nums ${r.direction === 'incoming' ? 'text-success' : 'text-error'}`}>
                            {fmtSR(Math.abs(r.amount))}
                          </span>
                        </div>
                      ))}
                      {g.rows.length > 6 && (
                        <div className="italic">+ {g.rows.length - 6} more…</div>
                      )}
                    </div>
                  </div>
                </label>
              </div>
            );
          })}

          {/* Unmatched summary — informational only, can't be applied */}
          {data?.groups?.find((g) => g.payee_type === 'unmatched') && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>{data.groups.find((g) => g.payee_type === 'unmatched').txn_count}</strong> txns
                ({fmtSR(data.groups.find((g) => g.payee_type === 'unmatched').total_amount)}) couldn't be auto-matched —
                add the missing supplier/customer/employee or review them in the bank statements page.
              </div>
            </div>
          )}

          {/* Last-applied summary */}
          {appliedSummary && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-900" data-testid="rec-month-applied-summary">
              <div className="font-semibold mb-1 flex items-center gap-1">
                <Banknote size={12} /> Apply summary
              </div>
              <div>Created: {appliedSummary.created} · Skipped (already linked): {appliedSummary.skipped} · Total: {fmtSR(appliedSummary.totalAmount)}</div>
              {appliedSummary.failed?.length > 0 && (
                <ul className="mt-1 list-disc list-inside text-rose-700">
                  {appliedSummary.failed.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rec-month-close">
            Close
          </Button>
          <Button
            onClick={apply}
            disabled={applying || selectedKeys.length === 0}
            data-testid="rec-month-apply"
          >
            {applying ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Applying…</>
              : <>Apply ({selectedKeys.length} group{selectedKeys.length === 1 ? '' : 's'} · {selectedTotals.txns} txn{selectedTotals.txns === 1 ? '' : 's'})</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReconcileMonthDialog;
